import * as Api from '@ViewModels';
import * as React from 'react';
import { css } from 'aphrodite';
import { filterDisplayStyles } from './filterDisplayStyles';

/**
 * Union type that accepts both IContactFilterCriteria and AutomationTemplateFilterCriteria
 * This allows filter display functions to work with both contact filters and automation template filters
 */
type FilterCriteria = Api.IContactFilterCriteria | Api.AutomationTemplateFilterCriteria;

/**
 * Maps contact filter property enum values to human-readable names.
 * Used for displaying contact filter properties in automation flow conditions.
 */
export function getPropertyName(
	property:
		| Api.AutomationContactFilterProperty
		| Api.AutomationTemplateFilterProperty
		| Api.ContactFilterCriteriaProperty
): string {
	const propertyMap: Record<number, string> = {
		[Api.AutomationContactFilterProperty.FirstName]: 'First Name',
		[Api.AutomationContactFilterProperty.LastName]: 'Last Name',
		[Api.AutomationContactFilterProperty.Email]: 'Email',
		[Api.AutomationContactFilterProperty.Phone]: 'Phone',
		[Api.AutomationContactFilterProperty.Company]: 'Company',
		[Api.AutomationContactFilterProperty.Title]: 'Title',
		[Api.AutomationContactFilterProperty.Tags]: 'Tags',
		[Api.AutomationContactFilterProperty.Source]: 'Source',
		[Api.AutomationContactFilterProperty.Status]: 'Status',
		[Api.AutomationContactFilterProperty.Tag]: 'Tag',
		[Api.AutomationContactFilterProperty.CustomField]: 'Custom Field',
	};
	const propertyAsNumber = property as number;
	return propertyMap[propertyAsNumber] || `Property ${propertyAsNumber}`;
}

/**
 * Gets operator text for filter criteria
 */
export function getOperatorText(operator: string | undefined, hasValue: boolean): string {
	switch (operator) {
		case 'Not':
			return hasValue ? 'is not' : 'does not exist';
		case 'Gte':
		case 'Gt':
			return 'is greater than or equal to';
		case 'Lte':
		case 'Lt':
			return 'is less than or equal to';
		default:
			return hasValue ? 'is' : 'exists';
	}
}

/**
 * Formats a single criteria for concise display (for edge labels)
 * Example: "Tag is VIP"
 */
function formatSingleCriteria(criteria: FilterCriteria): string {
	if (criteria.criteria && criteria.criteria.length > 0) {
		const groupOperator = criteria.op === 'Or' ? ' OR ' : ' AND ';
		return criteria.criteria.map(formatSingleCriteria).join(groupOperator);
	}

	const propertyName = criteria.property !== undefined ? getPropertyName(criteria.property) : 'Unknown';
	const value = typeof criteria.value === 'string' ? criteria.value : criteria.value ? String(criteria.value) : '';
	const operatorText = getOperatorText(criteria.op, !!value);

	if (value) {
		return `${propertyName} ${operatorText} "${value}"`;
	}
	return `${propertyName} ${operatorText}`;
}

/**
 * Formats filter condition for edge label display (concise, single line)
 * Example: "Tag is VIP Client"
 */
export function formatFilterCondition(filter?: FilterCriteria): string {
	if (!filter || !filter.criteria || filter.criteria.length === 0) {
		return 'All contacts';
	}

	const mainOperator = filter.op === 'Or' ? ' OR ' : ' AND ';
	const conditions = filter.criteria.map(formatSingleCriteria);

	if (conditions.length === 1) {
		return conditions[0];
	}

	if (conditions.join(mainOperator).length > 50) {
		return `${conditions[0]} +${conditions.length - 1} more`;
	}

	return conditions.join(mainOperator);
}

/**
 * Formats filter condition for node display (detailed, with structure)
 * Returns JSX for detailed display in condition nodes
 */
export function formatFilterForNode(filter?: FilterCriteria): React.ReactNode {
	if (!filter || !filter.criteria || filter.criteria.length === 0) {
		return <div className={css(filterDisplayStyles.allContactsText)}>All contacts</div>;
	}

	const renderCriteria = (criteria: FilterCriteria, depth = 0): React.ReactNode => {
		const indentStyle = { marginLeft: `${depth * 16}px` };

		if (criteria.criteria && criteria.criteria.length > 0) {
			const groupOperator = criteria.op === 'Or' ? 'OR' : 'AND';
			return (
				<div key={JSON.stringify(criteria)} style={indentStyle}>
					<div className={css(filterDisplayStyles.operatorText)}>{depth > 0 && `${groupOperator} (`}</div>
					<div style={{ marginLeft: depth > 0 ? 16 : 0 }}>
						{criteria.criteria.map((subCriteria: FilterCriteria, index: number) => (
							<div key={index}>
								{index > 0 && <div className={css(filterDisplayStyles.operatorDivider)}>{groupOperator}</div>}
								{renderCriteria(subCriteria, depth + 1)}
							</div>
						))}
					</div>
					{depth > 0 && (
						<div className={css(filterDisplayStyles.operatorText)} style={{ marginTop: 4 }}>
							)
						</div>
					)}
				</div>
			);
		}

		const propertyName = criteria.property !== undefined ? getPropertyName(criteria.property) : 'Unknown';
		const value = typeof criteria.value === 'string' ? criteria.value : criteria.value ? String(criteria.value) : '';
		const operatorText = getOperatorText(criteria.op, !!value);

		return (
			<div key={JSON.stringify(criteria)} className={css(filterDisplayStyles.criteriaItem)} style={indentStyle}>
				<span className={css(filterDisplayStyles.propertyName)}>{propertyName}</span>
				{operatorText && <span className={css(filterDisplayStyles.operatorLabel)}> {operatorText} </span>}
				{value && <span className={css(filterDisplayStyles.valueText)}>&quot;{value}&quot;</span>}
			</div>
		);
	};

	return (
		<div>
			{filter.criteria.map((criteria: FilterCriteria, index: number) => (
				<div key={index}>
					{index > 0 && (
						<div className={css(filterDisplayStyles.operatorDivider)}>{filter.op === 'Or' ? 'OR' : 'AND'}</div>
					)}
					{renderCriteria(criteria)}
				</div>
			))}
		</div>
	);
}

/**
 * Gets a brief summary of filter for switch node overview display
 * Example: "2 conditions: Tag, Status"
 */
export function getFilterSummary(filter?: FilterCriteria): string {
	if (!filter || !filter.criteria || filter.criteria.length === 0) {
		return 'No conditions';
	}

	const countCriteria = (criteria: FilterCriteria[]): number => {
		return criteria.reduce((count, c) => {
			if (c.criteria && c.criteria.length > 0) {
				return count + countCriteria(c.criteria);
			}
			return count + 1;
		}, 0);
	};

	const totalCount = countCriteria(filter.criteria);

	const getFirstProperties = (criteria: FilterCriteria[]): string[] => {
		const props: string[] = [];
		for (const c of criteria) {
			if (c.criteria && c.criteria.length > 0) {
				props.push(...getFirstProperties(c.criteria));
			} else if (c.property !== undefined) {
				props.push(getPropertyName(c.property));
			}
			if (props.length >= 2) {
				break;
			}
		}
		return props;
	};

	const firstProps = getFirstProperties(filter.criteria);

	if (totalCount === 1) {
		return firstProps[0] || 'condition';
	}

	if (firstProps.length === 1) {
		return `${totalCount} conditions: ${firstProps[0]} +${totalCount - 1} more`;
	}

	return `${totalCount} conditions: ${firstProps.join(', ')}${totalCount > 2 ? ` +${totalCount - 2} more` : ''}`;
}

/**
 * Renders filter criteria in a human-readable format with styling
 * Used for detailed display in panels and cards
 */
export function renderFilterCriteria(filter?: FilterCriteria): React.ReactNode {
	if (!filter || !filter.criteria || filter.criteria.length === 0) {
		return null;
	}

	return (
		<div className={css(filterDisplayStyles.conditionContainer)}>
			<div className={css(filterDisplayStyles.conditionHeader)}>Condition:</div>
			{formatFilterForNode(filter)}
		</div>
	);
}
